import React from "react";
import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { formatAbsolute } from "../common/hooks";
import {
  getLifecycleProgress,
  getLifecycleStepTimestamps,
  LIFECYCLE_STEPS,
} from "../common/lifecycleSteps";

const formatStepStamp = (iso) => {
  const text = formatAbsolute(iso);
  if (!iso || text === "—") return null;
  const splitAt = text.lastIndexOf(", ");
  if (splitAt < 0) return text;
  return (
    <>
      {text.slice(0, splitAt)}
      <br />
      {text.slice(splitAt + 2)}
    </>
  );
};

/**
 * Horizontal milestone stepper — highlights where this task is in the pipeline.
 */
const StatusProgressStepper = ({
  status,
  timeline,
  createdAt,
  dryRunReadyAt,
  statusEnteredAt,
}) => {
  const { activeIndex, terminal, terminalLabel } = getLifecycleProgress(status);
  const lastIndex = LIFECYCLE_STEPS.length - 1;
  const stepTimestamps = getLifecycleStepTimestamps({
    timeline,
    created_at: createdAt,
    dry_run_ready_at: dryRunReadyAt,
    status,
    status_entered_at: statusEnteredAt,
  });

  return (
    <Box sx={{ mb: 2.5, overflowX: "auto", pb: 0.5 }}>
      <Stepper
        activeStep={activeIndex}
        alternativeLabel
        sx={{
          minWidth: 560,
          "& .MuiStepLabel-label": {
            fontSize: 11,
            fontWeight: 500,
            mt: 0.5,
          },
          "& .MuiStepLabel-label.Mui-active": {
            fontWeight: 700,
            color: terminal === "failed" ? "error.main" : terminal === "cancelled" ? "text.secondary" : "success.main",
          },
          "& .MuiStepLabel-label.Mui-completed": {
            fontWeight: 500,
          },
          "& .MuiStepIcon-root.Mui-active": {
            color: terminal === "failed" ? "error.main" : terminal === "cancelled" ? "grey.500" : "success.main",
          },
          "& .MuiStepIcon-root.Mui-completed": {
            color: "success.main",
          },
        }}
      >
        {LIFECYCLE_STEPS.map((step, index) => {
          const isCurrent = index === activeIndex;
          const isSuccessLast = terminal === "success" && index === lastIndex;
          const label =
            isCurrent && terminal ? terminalLabel : step.label;
          const stamp = formatStepStamp(stepTimestamps[index]);
          return (
            <Step key={step.id} completed={index < activeIndex}>
              <StepLabel
                error={isCurrent && terminal === "failed"}
                optional={
                  stamp ? (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        fontSize: 10,
                        fontWeight: 400,
                        lineHeight: 1.35,
                        mt: 0.25,
                      }}
                    >
                      {stamp}
                    </Typography>
                  ) : null
                }
                sx={
                  isSuccessLast
                    ? {
                        "& .MuiStepLabel-label.Mui-completed": {
                          color: "success.main",
                          fontWeight: 700,
                        },
                      }
                    : undefined
                }
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default StatusProgressStepper;
